import { Button } from "@/components/ui/baseUi/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/baseUi/dropdown-menu"
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/baseUi/input-group"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/baseUi/tabs"
import { Text } from "@/components/ui/baseUi/text"
import { CoachOfferAccompaniedList } from "@/modules/coachOffers/components/coachOfferAccompaniedList"
import { CoachOfferRemoteList } from "@/modules/coachOffers/components/coachOfferRemoteList"
import { ProgramType } from "@/modules/programs/types/programTypes"
import { CoachOfferType } from "@modules/coachOffers/entities/coachOfferTypes"
import { useCoachOfferSidebar } from "@modules/coachOffers/hooks/useCoachOfferSidebar"
import { word } from "@modules/core/utils/i18n"
import { useQueryParam, useSetMultiParams } from "@modules/utils/navigation/useQueryParam"
import { ChevronDownIcon, SearchIcon, SettingsIcon, XIcon } from "lucide-react"
import { Activity, startTransition } from "react"

enum CoachOfferTab {
  Accompanied = "accompanied",
  Remote = "remote",
}

export const ALL_OFFER_FILTER = "ALL" as const
export type OfferTypeFilter =
  | typeof ALL_OFFER_FILTER
  | (typeof CoachOfferType)[keyof typeof CoachOfferType]
  | ProgramType.VIRTUAL
  | ProgramType.ONOFF
  | ProgramType.PRO

export const ACCOMPANIED_OFFER_TYPES = [CoachOfferType.Individual, CoachOfferType.Collective]
export const REMOTE_OFFER_TYPES = [ProgramType.VIRTUAL, ProgramType.ONOFF, ProgramType.PRO]

export function CoachOffersPage() {
  const [search, setSearch] = useQueryParam("search")
  const [typeParam] = useQueryParam("type")
  const [filterParam] = useQueryParam("filterType")
  const setMultiParams = useSetMultiParams()

  const selectedTab = (typeParam ?? CoachOfferTab.Accompanied) as CoachOfferTab
  const selectedFilter = (filterParam ?? ALL_OFFER_FILTER) as OfferTypeFilter

  const { navigateToCoachOfferOrder } = useCoachOfferSidebar()

  const handleFilterChange = (filter: OfferTypeFilter) => {
    startTransition(() => {
      if (filter === ALL_OFFER_FILTER) {
        setMultiParams([{ key: "filterType" }])
      } else {
        const isAccompanied = filter === CoachOfferType.Individual || filter === CoachOfferType.Collective
        setMultiParams([
          { key: "filterType", value: filter },
          { key: "type", value: isAccompanied ? CoachOfferTab.Accompanied : CoachOfferTab.Remote },
        ])
      }
    })
  }

  const handleTabChange = (value: string) => {
    startTransition(() => {
      setMultiParams([{ key: "type", value }, { key: "filterType" }])
    })
  }

  const FILTER_LABELS: Record<OfferTypeFilter, string> = {
    ALL: word("coach.offers.filter.all"),
    [CoachOfferType.Individual]: word("coach.offers.type.INDIVIDUAL"),
    [CoachOfferType.Collective]: word("coach.offers.type.COLLECTIVE"),
    [ProgramType.VIRTUAL]: word("coach.offers.type.CONTINUOUS"),
    [ProgramType.ONOFF]: word("coach.offers.type.ON_OFF"),
    [ProgramType.PRO]: word("coach.offers.type.PRO"),
  }

  return (
    <div className="flex w-full flex-col gap-8 p-6">
      {/* HEADER */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <div className="flex items-baseline gap-2">
            <Text fontStyle="HM" className="text-textOnSurface-default">
              {word("coach.offers.page.title")}
            </Text>
            <Text fontStyle="HS" className="text-textOnSurface-default">
              /
            </Text>
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <button type="button" className="flex cursor-pointer items-center gap-1">
                    <Text fontStyle="HS" className="text-textOnSurface-default">
                      {FILTER_LABELS[selectedFilter]}
                    </Text>
                    <ChevronDownIcon className="size-5 text-textOnSurface-default" />
                  </button>
                }
              />
              <DropdownMenuContent align="start" className="min-w-48">
                <DropdownMenuItem
                  className="pl-8 font-semibold"
                  onClick={() => {
                    handleFilterChange(ALL_OFFER_FILTER)
                  }}
                >
                  {FILTER_LABELS[ALL_OFFER_FILTER]}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuLabel className="text-xs font-normal text-textOnSurface-muted">
                    {word("coach.offers.accompanied")}
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    className="pl-8"
                    onClick={() => {
                      handleFilterChange(CoachOfferType.Individual)
                    }}
                  >
                    {FILTER_LABELS[CoachOfferType.Individual]}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="pl-8"
                    onClick={() => {
                      handleFilterChange(CoachOfferType.Collective)
                    }}
                  >
                    {FILTER_LABELS[CoachOfferType.Collective]}
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuLabel className="text-xs font-normal text-textOnSurface-muted">
                    {word("coach.offers.remote")}
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    className="pl-8"
                    onClick={() => {
                      handleFilterChange(ProgramType.VIRTUAL)
                    }}
                  >
                    {FILTER_LABELS[ProgramType.VIRTUAL]}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="pl-8"
                    onClick={() => {
                      handleFilterChange(ProgramType.ONOFF)
                    }}
                  >
                    {FILTER_LABELS[ProgramType.ONOFF]}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="pl-8"
                    onClick={() => {
                      handleFilterChange(ProgramType.PRO)
                    }}
                  >
                    {FILTER_LABELS[ProgramType.PRO]}
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <Text fontStyle="BM" className="text-textOnSurface-muted">
            {word("coach.offers.page.subtitle")}
          </Text>
        </div>
        <InputGroup className="max-w-68">
          <InputGroupAddon align="inline-start">
            <SearchIcon />
          </InputGroupAddon>
          <InputGroupInput
            placeholder={word("global.search.placeholder")}
            value={search ?? ""}
            onChange={(e) => {
              setSearch(e.target.value)
            }}
          />
          {search && (
            <InputGroupAddon align="inline-end">
              <XIcon
                className="size-4 cursor-pointer"
                onClick={() => {
                  setSearch("")
                }}
              />
            </InputGroupAddon>
          )}
        </InputGroup>
      </div>

      {/* TABS */}
      <Tabs value={selectedTab} onValueChange={handleTabChange} className="relative flex w-full flex-col gap-6">
        <div className="flex items-end gap-4">
          <TabsList variant="default" className="flex-1 border-interactive-border-neutral-faded">
            <TabsTrigger value={CoachOfferTab.Accompanied} variant="default">
              {word("coach.offers.accompanied")}
            </TabsTrigger>
            <TabsTrigger value={CoachOfferTab.Remote} variant="default">
              {word("coach.offers.remote")}
            </TabsTrigger>
          </TabsList>
          <Button variant="secondarySecondary" size="iconLg" onClick={() => void navigateToCoachOfferOrder()}>
            <SettingsIcon />
          </Button>
        </div>

        {/* CONTENT - OFFERS LIST */}
        <TabsContent value={selectedTab}>
          <Activity mode={selectedTab === CoachOfferTab.Accompanied ? "visible" : "hidden"}>
            <CoachOfferAccompaniedList selectedFilter={selectedFilter} />
          </Activity>

          <Activity mode={selectedTab === CoachOfferTab.Remote ? "visible" : "hidden"}>
            <CoachOfferRemoteList selectedFilter={selectedFilter} />
          </Activity>
        </TabsContent>
      </Tabs>
    </div>
  )
}
